import { useEffect, useState } from 'react';
import { Button, Card, Col, Container, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';

import { getFeaturedScripts, FeaturedError, FeaturedScript } from '../utils/Featured';

import { LoadingRing } from '../components/LoadingRing';

import useUserStore from '../stores/User';

/** Public index of every currently featured script. No account required. */
function FeaturedPage() {
    const isLoggedIn = useUserStore((state) => state.isLoggedIn());

    const [scripts, setScripts] = useState<FeaturedScript[]>([]);
    const [error, setError] = useState<FeaturedError | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        getFeaturedScripts().then(({ scripts: found, error: loadError }) => {
            setScripts(found);
            setError(loadError);
            setLoading(false);
        });
    }, []);

    return (
        <Container fluid className="px-3 px-sm-5 mb-5">
            <Row>
                <Col xs={12}>
                    <h1>Featured Scripts</h1>
                    <p className="text-muted">
                        A hand-picked selection from the Script Hive library, free to read without an
                        account. The full collection of over 27,000 film and TV screenplays is available
                        to anyone with a free account.
                    </p>
                </Col>
            </Row>

            {loading ? (
                <LoadingRing />
            ) : error ? (
                <Row>
                    <Col xs={12} className="text-center py-5">
                        <i className="bi bi-cloud-slash text-muted" style={{ fontSize: '3rem' }}></i>
                        <p className="text-muted mt-3">
                            {error === 'rateLimited'
                                ? "We've seen a lot of requests from your network. Please try again in a minute."
                                : "We couldn't load the featured scripts right now. Please try again shortly."}
                        </p>
                    </Col>
                </Row>
            ) : scripts.length === 0 ? (
                <Row>
                    <Col xs={12} className="text-center py-5">
                        <i className="bi bi-star text-muted" style={{ fontSize: '3rem' }}></i>
                        <p className="text-muted mt-3">No scripts are featured at the moment. Check back soon.</p>
                    </Col>
                </Row>
            ) : (
                <Row xs={1} md={2} xl={3} className="g-3">
                    {scripts.map((script) => (
                        <Col key={script.slug}>
                            <Card className="h-100">
                                <Card.Body className="d-flex flex-column">
                                    <Card.Title className="fs-6 fw-bold text-break">{script.headline}</Card.Title>
                                    <Card.Subtitle className="mb-2 text-muted small text-break">
                                        {`Written by ${script.writers || 'Unlisted'}`}
                                    </Card.Subtitle>
                                    {script.blurb && (
                                        <Card.Text className="text-muted small text-break">{script.blurb}</Card.Text>
                                    )}
                                    <div className="mt-auto pt-3">
                                        <Link to={`/featured/${script.slug}`}>
                                            <Button variant="primary" size="sm">
                                                <i className="bi bi-book me-2"></i>Read script
                                            </Button>
                                        </Link>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}

            <Row className="mt-5">
                <Col xs={12}>
                    <h2 className="fs-5">Want the whole library?</h2>
                    <p className="text-muted">
                        Script Hive is free for screenwriters to study produced screenplays and improve
                        their craft.
                    </p>
                    <Link to={isLoggedIn ? '/database' : '/register'}>
                        <Button variant="primary">
                            {isLoggedIn ? 'Browse the library' : 'Create a free account'}
                        </Button>
                    </Link>
                </Col>
            </Row>
        </Container>
    );
}

export default FeaturedPage;
